import { useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { useDeleteVenueClosure } from '@/hooks/useDeleteVenueClosure'
import { ApiError, toApiError } from '@/lib/api/errors'
import type { VenueClosureItemView } from '@/lib/venue/closureTypes'

const CONFIRM_NOTICE = '해당 일정을 삭제하면 해당 시간이 즉시 예약 가능 상태로 전환됩니다'

/**
 * 휴무·차단·레슨 해제 확인 모달(요구사항 원문 문구, 설계 §2). 409(기능 차단)면 모달 안에서
 * 서버 메시지로 대체 표시한다(`CourtDeleteConfirmModal.tsx` 와 동일한 패턴).
 */
export function ClosureDeleteConfirmModal({
  venueId,
  item,
  onClose,
}: {
  venueId: string
  item: VenueClosureItemView
  onClose: () => void
}) {
  const [error, setError] = useState<ApiError | null>(null)
  const deleteMutation = useDeleteVenueClosure(venueId)

  async function handleConfirm() {
    setError(null)
    try {
      await deleteMutation.mutateAsync(item.closureId)
      onClose()
    } catch (thrown) {
      setError(toApiError(thrown))
    }
  }

  return (
    <Modal title="일정 삭제" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-slateish-600">
          <span className="font-medium text-ink-700">{item.periodLabel}</span> 일정을 삭제할까요?
        </p>

        {error ? (
          <Alert tone="danger">
            <p>{error.message}</p>
          </Alert>
        ) : (
          <Alert tone="warning">{CONFIRM_NOTICE}</Alert>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="border-danger-500 text-danger-500 hover:bg-danger-100"
            disabled={deleteMutation.isPending}
            onClick={() => {
              void handleConfirm()
            }}
          >
            {deleteMutation.isPending ? '삭제하는 중…' : '삭제'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
